import React, { useCallback, useState } from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { noop } from '../../../default-props/noop';
import { emptyObject } from 'ui-components/default-props/empty';

import { Checkbox } from '../../checkbox';
import { Button } from '../../button';
import { DisplayGridCell } from '../display-grid-cell/index';
import { Popover } from 'ui-components/components/popover';
import { GroupbyActionPopover } from '../display-grid-body/groupby-actions-popover';

import rowStyle from '../display-grid-body/index.module.less';
import { messages } from '../display-grid-body/messages';
import style from './index.module.css';

import { DisplayGridRecordPropType } from '../../../prop-types/display-grid-record';
import { DisplayGridGroupByRowMetadata } from '../../../prop-types/display-grid-group-record';

const checkboxCol = { id: 'checkbox' };

export const DisplayGridCheckboxCell = React.memo(
  ({
    checked = false,
    className,
    disabled = false,
    indeterminate = false,
    onChange = noop,
    onClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    shouldRenderToggler = false,
    renderCheckbox = false,
    toggleRow = noop,
    isExpanded = false,
    dataRowId,
    data = emptyObject,
    totalSelectedGroups = null,
    clickTotalSelectGroup = noop,
  }) => {
    const [isGroupByPopoverOpen, setIsGroupbyPopoverOpen] = useState(false);
    const toggleGroupbyPopover = useCallback(() => setIsGroupbyPopoverOpen((isOpen) => !isOpen), []);
    return (
      <DisplayGridCell
        className={classnames(style.checkbox, className, {
          [style['is-groupby-header']]: data.groupById,
        })}
        column={checkboxCol}
        dataRowId={dataRowId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {shouldRenderToggler && (
          <Button
            className={classnames(rowStyle.expander, {
              [rowStyle.expanded]: isExpanded,
            })}
            data-role='expander'
            onClick={toggleRow}
            theme='link'
          >
            <span>&rsaquo;</span>
          </Button>
        )}
        {renderCheckbox ? (
          <Checkbox checked={checked} disabled={disabled} indeterminate={indeterminate} onChange={onChange} onClick={onClick} />
        ) : (
          <div style={{ content: '', width: '14px' }} />
        )}
        {data.groupByDetails &&
          data.groupByDetails.map((detail, index) => (
            <span key={`groupByRowDetails${index}-${detail.groupByValue}`} className={style['groupby-header-detail']}>
              <b>
                <FormattedMessage {...detail.groupByName} />:
              </b>
              {detail.groupByValue || <FormattedMessage {...messages.none} />}
            </span>
          ))}
        {data.groupById && (
          <div className={style['groupby-header-options']}>
            <span className={style['groupby-header-detail']}>
              {data.selectedRecordCount === 0 && (
                <FormattedMessage {...messages.groupOrdersCount} values={{ totalCount: data.totalRecordCount }} />
              )}
              {data.selectedRecordCount > 0 && (
                <FormattedMessage
                  {...messages.groupOrdersSelectedCount}
                  values={{
                    selectedCount: totalSelectedGroups.has(data.groupById) ? data.totalRecordCount : data.selectedRecordCount,
                    totalCount: data.totalRecordCount,
                  }}
                />
              )}
            </span>
            {checked && data.selectedRecordCount < data.totalRecordCount && !totalSelectedGroups.has(data.groupById) && (
              <Button className={style['groupby-header-detail']} onClick={clickTotalSelectGroup} theme='link'>
                <FormattedMessage {...messages.groupOrdersSelectAll} values={{ totalCount: data.totalRecordCount }} />
              </Button>
            )}
            {data.selectedRecordCount > 0 && (
              <Popover
                appendTarget={document.querySelector('#app-root')}
                body={
                  <GroupbyActionPopover
                    closePopover={toggleGroupbyPopover}
                    groupByDetails
                    hasSelectedSpilloverItems={totalSelectedGroups.has(data.groupById)}
                  />
                }
                enterExitTransitionDurationMs={0}
                isOpen={isGroupByPopoverOpen}
                onOuterAction={toggleGroupbyPopover}
                preferPlace='above'
              >
                <Button className={style['groupby-header-detail']} onClick={toggleGroupbyPopover} theme='link'>
                  <FormattedMessage {...messages.groupOrdersMoreOptions} />
                </Button>
              </Popover>
            )}
          </div>
        )}
      </DisplayGridCell>
    );
  },
);

DisplayGridCheckboxCell.propTypes = {
  checked: PropTypes.bool,
  className: PropTypes.string,
  dataRowId: PropTypes.string,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  shouldRenderToggler: PropTypes.bool,
  renderCheckbox: PropTypes.bool,
  toggleRow: PropTypes.func,
  isExpanded: PropTypes.bool,
  data: PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupByRowMetadata]),
  totalSelectedGroups: PropTypes.instanceOf(Set),
  clickTotalSelectGroup: PropTypes.func,
};
